import { FC, ReactNode, useEffect } from "react";

import {
  useHookForm,
  useMerchantApiMutation,
  useNotifications,
} from "../../../../hooks";
import {
  IRefundPaymentIssueToMerchantBalance,
  IHookForm,
  IViewVirtualAccountTransferRefund,
} from "../../../../interfaces-and-types";
import { globalHandlerUtils } from "../../../../utils";

import { View } from "./View";
import { acceptAsPartialSchema } from "./validationSchema";

export interface ISettlePaymentIssueToMerchantBalanceFormViewProps
  extends IHookForm<IRefundPaymentIssueToMerchantBalance> {
  // current state of loading view
  isLoadingFormData: boolean;
  isFormEnabled: boolean;
  formDisabledReason: ReactNode | null;
  refundAmount: number;
  refundCurrency: string;
}

export interface ISettlePaymentIssueToMerchantBalanceFormProps {
  // current state of loading view
  isLoadingFormData: boolean;
  isFormEnabled: boolean;
  formDisabledReason: ReactNode | null;
  // refund details
  refundAmount: number;
  refundCurrency: string;
  // form valuess
  transferRefundId: string;
}

export const RefundToMerchantBalanceForm: FC<
  ISettlePaymentIssueToMerchantBalanceFormProps
> = (props) => {
  const notificationsHandler = useNotifications();

  const {
    isLoading: isLoadingSettleToMerchantBalance,
    isSuccess: isSuccessSettleToMerchantBalance,
    // isError: isErrorSettleToMerchantBalance,
    data: settleToMerchantBalance,
    mutate: acceptAsPartialPayment,
  } = useMerchantApiMutation<
    IRefundPaymentIssueToMerchantBalance,
    IViewVirtualAccountTransferRefund
  >(
    "settlePaymentIssueToMerchantBalance",
    ["getVirtualAccountTransferRefundById"],
    {
      // ignoreActivityBlocker: true,
    },
    (urlTemplate) => urlTemplate.replace("id", props.transferRefundId)
  ).usePostMutation;

  const initialFormValues: IRefundPaymentIssueToMerchantBalance = {
    transfer_refund_id: props.transferRefundId,
    description: "",
  };

  const handleApiSubmit = (data: IRefundPaymentIssueToMerchantBalance) => {
    acceptAsPartialPayment(data);
  };

  const handleError = (errors: any) => globalHandlerUtils.handleError(errors);

  const { onFormSubmit, errors, register, reset } = useHookForm({
    initialFormValues,
    validationSchema: acceptAsPartialSchema,
    onValid: handleApiSubmit,
    onInvalid: handleError,
  });

  errors && console.warn("RefundToMerchantBalanceForm.errors: ", errors);

  useEffect(() => {
    reset(initialFormValues);
  }, [props.transferRefundId]);

  useEffect(() => {
    if (isSuccessSettleToMerchantBalance) {
      reset();

      // display a notification of success
      notificationsHandler.showNotification({
        ariaLabel: "info notification",
        title: "Payment Issue Settled To Balance Successfully",
        kind: "info",
        message: `The payment issue has been resolved. It has been settled to your ${props.refundCurrency} balance.`,
        statusIconDescription: "notification",
        timeout: 10000,
      });
    }
  }, [isSuccessSettleToMerchantBalance]);

  return (
    <View
      {...props}
      handleSubmit={onFormSubmit}
      register={register}
      errors={errors}
    />
  );
};
